import React, { useRef, useEffect, useState } from 'react';
import { VideoInfo } from "#/types/message";
import styles from './video-container.module.css';

declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
    youtubeAPILoading?: boolean;
    youtubeAPICallbacks?: (() => void)[];
  }
}

interface VideoContainerProps {
  video: VideoInfo;
  maxDisplayWidth: number | null;
  maxDisplayHeight: number;
  internalActiveIndex: number;
  onHeightReady?: (height: number) => void;
}

const VideoContainer: React.FC<VideoContainerProps> = ({
  video,
  maxDisplayWidth,
  maxDisplayHeight,
  onHeightReady,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [apiReady, setApiReady] = useState(false);
  const [playerReady, setPlayerReady] = useState(false);
  const touchStartPos = useRef<{ x: number; y: number; time: number } | null>(null);

  const aspectRatio = 16 / 9;
  const metadataHeight = (video.metadata?.title || video.metadata?.subtitle) ? 60 : 0;
  const availableHeightForVideo = maxDisplayHeight - metadataHeight;

  let maxWidth = availableHeightForVideo * aspectRatio;
  if (maxDisplayWidth) {
    maxWidth = Math.min(maxWidth, maxDisplayWidth);
  }

  const finalVideoHeight = Math.min(availableHeightForVideo, maxWidth / aspectRatio);
  const finalVideoWidth = finalVideoHeight * aspectRatio;

  // Load YouTube API only once globally
  useEffect(() => {
    const loadYouTubeAPI = () => {
      if (window.YT && window.YT.Player) {
        setApiReady(true);
        return;
      }

      if (!window.youtubeAPICallbacks) {
        window.youtubeAPICallbacks = [];
      }
      window.youtubeAPICallbacks.push(() => setApiReady(true));

      if (!window.youtubeAPILoading) {
        window.youtubeAPILoading = true;

        const script = document.createElement('script');
        script.src = 'https://www.youtube.com/iframe_api';
        script.async = true;
        document.body.appendChild(script);

        window.onYouTubeIframeAPIReady = () => {
          if (window.youtubeAPICallbacks) {
            window.youtubeAPICallbacks.forEach(callback => callback());
            window.youtubeAPICallbacks = [];
          }
        };
      }
    };

    loadYouTubeAPI();

    return () => {
      if (playerRef.current && playerRef.current.destroy) {
        try {
          playerRef.current.destroy();
        } catch (error) {
          console.error('Error destroying player:', error);
        }
      }
    };
  }, []);

  // Create YouTube player when API is ready
  useEffect(() => {
    if (apiReady && playerContainerRef.current && window.YT && window.YT.Player) {
      if (playerRef.current && playerRef.current.destroy) {
        try {
          playerRef.current.destroy();
        } catch (error) {
          console.error('Error destroying existing player:', error);
        }
      }

      const playerId = `youtube-player-${video.video_id}-${Date.now()}`;
      playerContainerRef.current.id = playerId;

      try {
        playerRef.current = new window.YT.Player(playerId, {
          width: finalVideoWidth,
          height: finalVideoHeight,
          videoId: video.video_id,
          playerVars: {
            rel: 0,
            modestbranding: 1,
            iv_load_policy: 3,
            cc_load_policy: 0,
            fs: 1,
            disablekb: 0,
            autoplay: 0,
            controls: 1,
            start: video.metadata?.start_time || undefined,
            end: video.metadata?.end_time || undefined,
          },
          events: {
            onReady: () => {
              console.log('YouTube player ready');
              setPlayerReady(true);
            },
            onStateChange: (event: any) => {
              const YT = window.YT;
              if (event.data === YT.PlayerState.PLAYING) {
                setIsVideoPlaying(true);
              } else if (event.data === YT.PlayerState.PAUSED || event.data === YT.PlayerState.ENDED) {
                setIsVideoPlaying(false);
              }
            },
          },
        });
      } catch (error) {
        console.error('Failed to create YouTube player:', error);
      }
    }
  }, [apiReady, video.video_id, finalVideoWidth, finalVideoHeight]);

  useEffect(() => {
    if (playerReady && containerRef.current) {
      const height = containerRef.current.offsetHeight;
      onHeightReady?.(height);
    }
  }, [playerReady, finalVideoHeight, metadataHeight, onHeightReady]);


  const handleOverlayTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchStartPos.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };
  };

  const handleOverlayTouchEnd = (e: React.TouchEvent) => {
    if (!touchStartPos.current || !playerRef.current) return;

    const touch = e.changedTouches[0];
    const deltaX = Math.abs(touch.clientX - touchStartPos.current.x);
    const deltaY = Math.abs(touch.clientY - touchStartPos.current.y);
    const deltaTime = Date.now() - touchStartPos.current.time;

    const isTap = deltaX < 10 && deltaY < 10 && deltaTime < 300;

    if (isTap) {
      e.preventDefault();
      e.stopPropagation();
      try {
        if (isVideoPlaying) {
          playerRef.current.pauseVideo();
        } else {
          playerRef.current.playVideo();
        }
      } catch (error) {
        console.error('Error controlling video:', error);
      }
    }

    touchStartPos.current = null;
  };

  return (
    <div
      ref={containerRef}
      className={styles.videoContainer}
    >
      {/* Metadata section above the video */}
      {(video.metadata?.title || video.metadata?.subtitle) && (
        <div className={styles.videoMetadata}>
          {video.metadata?.title && <div className={styles.videoTitle}>{video.metadata?.title}</div>}
          {video.metadata?.subtitle && <div className={styles.videoSubtitle}>{video.metadata?.subtitle}</div>}
        </div>
      )}

      {/* Video player */}
      <div className={styles.videoWrapper}>
        <div
          ref={playerContainerRef}
          className={styles.videoPlayer}
          style={{
            width: finalVideoWidth,
            height: finalVideoHeight,
            minHeight: finalVideoHeight,
          }}
        />

        {/* Overlay for touch handling - needed for the swipes to work on mobile */}
        {window.innerWidth <= 850 && (
          <div
            className={styles.videoOverlay}
            onTouchStart={handleOverlayTouchStart}
            onTouchEnd={handleOverlayTouchEnd}
          />
        )}

      </div>
    </div>
  );
};

export default VideoContainer; 